// Set up: the choices made when the program was built (layout from Ronan's
// capability prototype). Every change saves through the same handlers as the
// program card's editors, and with the same pickers, so the card on the board
// and this tab always show the same thing. Type and Format are locked once the
// program is published, so everyone gets the same program and results compare.

import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { toast } from 'react-toastify';
import { API } from '../../../../../../../api';
import DosenButton from '../../../../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import { UiInput, UiSelect } from '../../../../../../../components/ui/atoms';
import { useProgramContext } from '../../../../../../../hooks/useProgramContext';
import { useAppDispatch, useAppSelector } from '../../../../../../../redux/hooks';
import { grpDetailsActions } from '../../../../../../../redux/slices/group-details/groupDetails';
import ROLES from '../../../../../../../utils/roles';
import { getLLAuthHeaders } from '../../../../../utils/llAuthHeaders';
import { audienceRulesFromFlat, flattenAudienceRules, type AudienceRule } from '../../../../../utils/memberAttributes';
import ActionPill from '../../../../aimd-v2/components/ActionPill';
import { roleLockLabels, useRoleLock } from '../../../../capability-setup/create/roleLock';
import {
  AudiencePopover,
  LaunchByPopover,
  OwnerPopover,
  PROGRAM_TYPE_LABELS,
  ProgramTypePopover,
  formatCardDate,
  formatModelLabel
} from '../ProgramCard';
import { resolveAudienceLabels, useOrgAudienceOptions } from '../useOrgMemberAttributes';
import type { LearningPillar, ProgramCardData, ProgramOwner, ProgramTypeKey } from '../types';
import { Panel, t } from './editorUi';

const FORMAT_LABELS: Record<string, string> = { course: 'Course', shortCourse: 'Short Course', brief: 'Brief' };

export interface ProgramSetupTabProps {
  program: ProgramCardData;
  pillars: LearningPillar[];
  orgMembers: ProgramOwner[];
  onAudienceChange: (rules: AudienceRule[] | null) => void;
  onAudiencePreview: (rules: AudienceRule[]) => Promise<{ toAdd: number; toRemove: number } | null>;
  onLaunchDateChange: (launchDate: number | null) => void;
  onOwnerChange: (owner: ProgramOwner | null) => void;
  onProgramTypeChange: (programType: ProgramTypeKey) => void;
  onStageChange: (pillarId: string) => void;
  /** After a rename here, so the board shows the new name. */
  onRenamed: () => void;
}

// How a row is changed: in place (a field and Save), or with the card's own picker.
type RowEditor =
  | { kind: 'text'; save: (value: string) => Promise<boolean> }
  | {
      kind: 'select';
      options: { value: string; label: string }[];
      current: string;
      save: (value: string) => Promise<boolean>;
    }
  | { kind: 'popover'; open: (anchor: HTMLElement) => void };

interface Row {
  label: string;
  value: string | null;
  locked?: boolean;
  editor?: RowEditor;
}

const SetupRow: React.FC<{ row: Row; first: boolean }> = ({ row, first }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const { label, value, locked, editor } = row;

  const startEdit = () => {
    if (!editor) return;
    if (editor.kind === 'popover') {
      if (pillRef.current) editor.open(pillRef.current);
      return;
    }
    setDraft(editor.kind === 'select' ? editor.current : value ?? '');
    setEditing(true);
  };
  const save = async () => {
    if (!editor || editor.kind === 'popover') return;
    setSaving(true);
    const done = await editor.save(draft.trim());
    setSaving(false);
    if (done) setEditing(false);
  };

  return (
    <Box
      sx={{
        // Every row the same height, whether it shows a value, a field, or a long audience.
        height: 60,
        boxSizing: 'border-box',
        borderTop: first ? 'none' : `1px solid ${t.borderDim}`,
        display: 'grid',
        gridTemplateColumns: '150px minmax(0, 1fr) auto',
        gap: '16px',
        alignItems: 'center'
      }}
    >
      <Typography sx={{ fontSize: 13, color: t.textTertiary }}>{label}</Typography>
      {editing && editor?.kind === 'text' ? (
        <UiInput value={draft} onChange={(e) => setDraft(e.target.value)} />
      ) : editing && editor?.kind === 'select' ? (
        <UiSelect
          value={draft}
          onChange={(e) => setDraft(String(e.target.value))}
          options={editor.options}
          renderValue={(v) => editor.options.find((o) => o.value === v)?.label ?? ''}
        />
      ) : (
        <DosenTooltip title={value && value.length > 60 ? value : ''} placement="top-start">
          <Typography noWrap sx={{ fontSize: 14, color: value ? t.textPrimary : t.textTertiary, lineHeight: 1.5 }}>
            {value ?? 'Not set'}
          </Typography>
        </DosenTooltip>
      )}
      {locked ? (
        <DosenTooltip title="Locked once published.">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: t.textTertiary }}>
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: 12 }}>Locked</Typography>
          </Box>
        </DosenTooltip>
      ) : editing ? (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <DosenButton
            tone="basic"
            sizeVariant="sm"
            sx={{ minWidth: 90, borderColor: '#565656' }}
            onClick={() => setEditing(false)}
          >
            Cancel
          </DosenButton>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            sx={{ minWidth: 90 }}
            disabled={!draft.trim() || saving}
            onClick={save}
          >
            {saving ? 'Saving...' : 'Save'}
          </DosenButton>
        </Box>
      ) : editor ? (
        <span ref={pillRef}>
          <ActionPill icon={<EditIcon sx={{ fontSize: 14 }} />} label="Edit" onClick={startEdit} />
        </span>
      ) : (
        <span />
      )}
    </Box>
  );
};

const ProgramSetupTab: React.FC<ProgramSetupTabProps> = ({
  program,
  pillars,
  orgMembers,
  onAudienceChange,
  onAudiencePreview,
  onLaunchDateChange,
  onOwnerChange,
  onProgramTypeChange,
  onStageChange,
  onRenamed
}) => {
  const dispatch = useAppDispatch();
  const { location } = useProgramContext();
  const group = useAppSelector((s) => s.groupDetails.data);
  const { labelById, catalog } = useOrgAudienceOptions();
  const [audienceAnchor, setAudienceAnchor] = useState<HTMLElement | null>(null);
  const [launchByAnchor, setLaunchByAnchor] = useState<HTMLElement | null>(null);
  const [ownerAnchor, setOwnerAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);

  // The same rules the card edits: programs saved before audienceRules have only the flat list.
  const audienceRules =
    program.audienceRules && program.audienceRules.length > 0
      ? program.audienceRules
      : audienceRulesFromFlat(program.audience, catalog);
  // A Role Excellence program built for roles: its roles are its audience, and its type stays.
  const roleLock = useRoleLock(program.roleIds);
  const audienceLabels = roleLock
    ? roleLockLabels(roleLock)
    : resolveAudienceLabels(flattenAudienceRules(audienceRules), labelById);
  // The owner becomes a program admin, so only an org admin sets it (as on the card).
  const viewerRole = localStorage.getItem('role') || '';
  const canEditOwner = program.canEdit && viewerRole !== ROLES.grpAdmin && viewerRole !== ROLES.member;
  const published = program.status !== 'design' && program.status !== 'not-started';
  const isCourse = program.model === 'masterclass' || program.model === 'guided-masterclass';
  const editable = program.canEdit;

  // PATCH /programs/{id} renames it (409 when another program of the org has the name). The open
  // group takes the name too, so the Training tab's Program Name shows it.
  const rename = async (name: string): Promise<boolean> => {
    if (!name || name === program.name) return true;
    try {
      await API.patchProgram({ id: program.id, body: { name }, ...getLLAuthHeaders(), location });
      if (String(group?.grpId ?? '') === program.id) dispatch(grpDetailsActions.updateGroupInfo({ name }));
      onRenamed();
      toast.success('The program name is saved.');
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "The program name couldn't be saved. Please try again.");
      return false;
    }
  };

  const programRows: Row[] = [
    { label: 'Type', value: program.model ? formatModelLabel(program.model) : null, locked: published },
    { label: 'Name', value: program.name || null, editor: editable ? { kind: 'text', save: rename } : undefined },
    {
      label: 'Format',
      // A course built before Course / Short Course / Brief existed is a Course.
      value: program.format ? FORMAT_LABELS[program.format] ?? null : isCourse ? 'Course' : null,
      locked: published && (!!program.format || isCourse)
    },
    {
      label: 'Stage',
      value: pillars.find((p) => p.id === program.learningPillarId)?.name ?? null,
      editor: editable
        ? {
            kind: 'select',
            options: pillars.map((p) => ({ value: p.id, label: p.name })),
            current: program.learningPillarId,
            save: async (pillarId) => {
              if (pillarId && pillarId !== program.learningPillarId) onStageChange(pillarId);
              return true;
            }
          }
        : undefined
    },
    {
      label: 'Category',
      value: PROGRAM_TYPE_LABELS[program.programType] ?? null,
      editor: editable && !roleLock ? { kind: 'popover', open: setTypeAnchor } : undefined
    },
    {
      label: 'Owner',
      value: program.owner?.name ?? null,
      editor: canEditOwner ? { kind: 'popover', open: setOwnerAnchor } : undefined
    }
  ];
  const rows: Row[] = [
    ...programRows,
    {
      label: 'Audience',
      value: audienceLabels.length > 0 ? audienceLabels.join(', ') : null,
      editor: editable && !roleLock ? { kind: 'popover', open: setAudienceAnchor } : undefined
    },
    {
      label: 'Publish by',
      value: program.launchDate ? formatCardDate(program.launchDate) : null,
      editor: editable ? { kind: 'popover', open: setLaunchByAnchor } : undefined
    }
  ];

  return (
    // One Settings section in the middle of the page. The lock beside Type and Format says what can't change.
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <Panel sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: '20px', py: '14px', bgcolor: '#1C1C1C', borderBottom: `1px solid ${t.borderDim}` }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>Settings</Typography>
        </Box>
        <Box sx={{ px: '20px', bgcolor: '#0F0F0F' }}>
          {rows.map((row, i) => (
            <SetupRow key={row.label} row={row} first={i === 0} />
          ))}
        </Box>
      </Panel>

      {/* The program card's own pickers, saving through the same handlers. */}
      <AudiencePopover
        anchor={audienceAnchor}
        currentValue={audienceRules}
        catalog={catalog}
        onPreview={onAudiencePreview}
        onClose={() => setAudienceAnchor(null)}
        onSave={(next) => {
          onAudienceChange(next.length > 0 ? next : null);
          setAudienceAnchor(null);
        }}
      />
      <LaunchByPopover
        anchor={launchByAnchor}
        currentValue={program.launchDate}
        onClose={() => setLaunchByAnchor(null)}
        onSave={(next) => {
          onLaunchDateChange(next);
          setLaunchByAnchor(null);
        }}
      />
      <OwnerPopover
        anchor={ownerAnchor}
        existingMembers={orgMembers}
        currentOwnerId={program.owner?.id}
        onClose={() => setOwnerAnchor(null)}
        onSave={(next) => {
          onOwnerChange(next);
          setOwnerAnchor(null);
        }}
      />
      <ProgramTypePopover
        anchor={typeAnchor}
        currentValue={program.programType}
        onClose={() => setTypeAnchor(null)}
        onSave={(next) => {
          if (next !== program.programType) onProgramTypeChange(next);
          setTypeAnchor(null);
        }}
      />
    </Box>
  );
};

export default ProgramSetupTab;
